package ui.app.assign.detail.wheel.editor.widgets

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import api.ResourceOrientation
import api.ResourceType
import compose.assets.Svg
import compose.assets.svg.Close
import compose.assets.svg.Edit
import compose.assets.svg.Image_plus
import org.jetbrains.compose.resources.StringResource
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.layer_bottom_app_logo_icon
import ssoft.frontend.generated.resources.layer_bottom_app_logo_image
import tk.mallumo.compose.navigation.LocalNavigation
import tools.EFieldRegularLong
import tools.GlobalObjectShare
import tools.rememberString
import ui.resource.ResourceListingArgs
import ui.widget.LazyImage

@Composable
fun FieldCustomImage(
    companyId: Long,
    entryId: Long,
    canEdit: Boolean,
    image: EFieldRegularLong,
    label: StringResource,
    size: Dp,
    fixWidth: Boolean,
    orientation: ResourceOrientation,
    extraKeyImage: String = "",
    unsetDefault: ImageVector = Svg.Image_plus,
    showResetButton: Boolean = false,
    modifier: Modifier = Modifier,

    ) {

    val cardModifier = if (fixWidth) {
        Modifier.width(size)
            .aspectRatio(
                if (orientation == ResourceOrientation.HORIZONTAL) 16F / 9F
                else 9F / 16F,
                matchHeightConstraintsFirst = false
            )
    } else {
        Modifier.height(size)
            .aspectRatio(
                if (orientation == ResourceOrientation.HORIZONTAL) 16F / 9F
                else 9F / 16F,
                matchHeightConstraintsFirst = false
            )
    }

    FieldCustomImage(

        companyId = companyId,
        entryId = entryId,
        canEdit = canEdit,
        image = image,
        label = label,
        orientation = orientation,
        small = size < 192.dp,
        extraKeyImage = extraKeyImage,
        unsetDefault = unsetDefault,
        modifier = modifier,
        cardModifier = cardModifier,
        showResetButton = showResetButton
    )
}

@Composable
fun FieldCustomImage(
    companyId: Long,
    entryId: Long,
    canEdit: Boolean,
    image: EFieldRegularLong,
    label: StringResource,
    orientation: ResourceOrientation,
    small: Boolean,
    extraKeyImage: String = "",
    unsetDefault: ImageVector = Svg.Image_plus,
    modifier: Modifier = Modifier,
    cardModifier: Modifier = Modifier,
    showResetButton: Boolean = false,
    canBeIcon: Boolean = false
) {
    Box(modifier) {
        Card(shape = RoundedCornerShape(4.dp, 4.dp)) {
            Column {
                Text(
                    text = label.rememberString(),
                    style = MaterialTheme.typography.labelLarge,
                    modifier = Modifier.padding(8.dp)
                )

                Card(
                    shape = RoundedCornerShape(4.dp, 4.dp),
                    modifier = cardModifier
                ) {
                    Box(Modifier.fillMaxSize()) {
                        if (image.state == ResourceType.NO_IMAGE) {
                            Image(
                                imageVector = unsetDefault,
                                contentDescription = null,
                                modifier = Modifier.size(64.dp).align(Alignment.Center),
                                contentScale = ContentScale.Fit,
                                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.onSurface)
                            )
                        } else {
                            LazyImage(
                                id = image.state,
                                contentScale = ContentScale.FillBounds,
                                modifier = Modifier.fillMaxSize()
                            )
                            if (showResetButton) {
                                SmallFloatingActionButton(
                                    containerColor = MaterialTheme.colorScheme.errorContainer,
                                    contentColor = MaterialTheme.colorScheme.onErrorContainer,
                                    shape = CircleShape,
                                    onClick = { image.set(ResourceType.NO_IMAGE) },
                                    modifier = Modifier.align(Alignment.TopEnd)
                                        .padding(4.dp)
                                ) { Icon(Svg.Close, null) }
                            }
                        }
                    }
                }
            }
        }

        AddIconAction(
            companyId = companyId,
            entryId = entryId,
            canEdit = canEdit,
            image = image,
            small = small,
            orientation = orientation,
            extraKeyImage = extraKeyImage,
            canBeIcon = canBeIcon,
            modifier = Modifier.align(Alignment.BottomEnd)
        )
    }
}

@Composable
fun AddIconAction(
    canEdit: Boolean,
    companyId: Long,
    entryId: Long,
    image: EFieldRegularLong,
    small: Boolean,
    orientation: ResourceOrientation,
    extraKeyImage: String = "",
    modifier: Modifier,
    canBeIcon: Boolean
) {

    val nav = LocalNavigation.current
    val imagePickerKey = remember(entryId) {
        "${image.fieldName}:${entryId}:$extraKeyImage"
    }

    GlobalObjectShare.Consumer(imagePickerKey)


    fun openResourcePicker() {
        GlobalObjectShare.launchImagePicker(
            key = imagePickerKey,
            nav = nav,
            args = ResourceListingArgs(
                companyId = companyId,
                restrictionOrientation = orientation.mask,
                restrictionType = ResourceType.IMAGE.mask,
                restrictionCanIgnoreAspectRatio = true
            )
        ) {
            it.firstOrNull()?.apply {
                image.set(id)
            }
        }
    }

    fun openIconPicker() {
        GlobalObjectShare.launchIconPicker(
            key = imagePickerKey,
            nav = nav
        ) {
            image.set(it.id)
        }
    }
    if (canEdit) {
        Box(modifier.padding(4.dp)) {
            var showDropDown by remember { mutableStateOf(false) }
            if (canBeIcon) {
                DropdownMenu(
                    expanded = showDropDown,
                    onDismissRequest = { showDropDown = false }) {
                    DropdownMenuItem(
                        text = { Text(Res.string.layer_bottom_app_logo_icon.rememberString()) },
                        onClick = {
                            openIconPicker()
                            showDropDown = false
                        })
                    DropdownMenuItem(
                        text = { Text(Res.string.layer_bottom_app_logo_image.rememberString()) },
                        onClick = {
                            openResourcePicker()
                            showDropDown = false
                        })
                }
            }
            val onClick = {
                if (canBeIcon) showDropDown = true
                else openResourcePicker()
            }
            if (small) {
                SmallFloatingActionButton(
                    onClick = onClick,
                    modifier = modifier.padding(4.dp)
                ) {
                    Icon(
                        imageVector = Svg.Edit,
                        contentDescription = null
                    )
                }
            } else {
                FloatingActionButton(
                    onClick = onClick,
                    modifier = modifier.padding(4.dp)
                ) {
                    Icon(
                        imageVector = Svg.Edit,
                        contentDescription = null
                    )
                }
            }
        }

    }
}